import PropTypes from 'prop-types';
import React, {Component} from 'react';
import InputMask from 'react-input-mask';
import './TextField.less';
import { injectIntl, intlShape } from 'react-intl';
import _ from 'lodash';

export class TextField extends Component {
    static propTypes = {
        id: PropTypes.string,
        type: PropTypes.string,
        label: PropTypes.string,
        arialabel: PropTypes.string,
        ariadescribedby: PropTypes.string,
        toolTip: PropTypes.string,
        autoCapitalize: PropTypes.string,
        required: PropTypes.bool,
        mask: PropTypes.string,
        autoComplete: PropTypes.string,
        autoFocus: PropTypes.string,
        autoCorrect: PropTypes.string,
        disabled: PropTypes.string,
        maxLength: PropTypes.string,
        pattern: PropTypes.string,
        showTickMark:PropTypes.bool,
        POBox: PropTypes.object,
        validationError:PropTypes.shape({
            className: PropTypes.string,
            msg: PropTypes.string
        }),
        intl: intlShape.isRequired,

        // Below props come from Field HOC
        input: PropTypes.object.isRequired,
        meta: PropTypes.object.isRequired
    };

    /**
     * hints are used to show the user what is expected but in a calm background (not red)
     * useful for password input
     */
    getValidationHintsToShow = Hints => {
        const hints = [];

        for (const hint of Hints) {
            if (hint.subClass === 'show') {
                hints.push(hint.msg);
            }
        }
        return hints;
    };

    showValidationHints = hints => {
        const msgs = [];
        for (const hint of hints) {
            if (hint.subClass === 'show') {
                //process the group items with recursive call
                msgs.push(
                    //todo consider using dangerouslySetInnerHTML here for more flexibility
                    <li className={`${hint.subClass}`} key= {`${hint.msg}`}>{hint.msg}</li>
                );
            }
        }
        return msgs;
    };

    getValidationErrorsToShow = Errors => {
        const errors = [];

        for (const error of Errors) {
            if (error.subClass === 'show') {
                errors.push(error.msg);
            }
        }
        return errors;
    };

    showValidationErrors = (Errors, id) => {
        const msg = [];
        const errorsToShow = this.getValidationErrorsToShow(Errors);

        for (const errorMessage of errorsToShow) {
            msg.push(<p key={errorMessage} className="help-error error-empty open" id={`${id}Format'`}>{errorMessage}</p>);
        }
        return msg;
    };

    getLabelElt = () => {
        const { id, label, input } = this.props;
        const labelClass = input.value ? 'input-has-value' : '';
        let labelElt = '';

        if (label && id) {
            labelElt = <label className={labelClass} htmlFor={id}>{label}</label>;
        }
        return labelElt;
    };

    getErrorMsgElt = () => {
        const {
            id, POBox, validationError, intl, meta: { touched, active, error }
        } = this.props;

        let errorMsgElt = '';

        /****************************************************************
         * errors that should be shown whether field active or inactive
         ****************************************************************/

        if (POBox && touched && error && error.className === 'hasError poBoxError') {
            const addressValidationId = `${id}AddressValidation`;
            // this.errorString represents the error message, whether it's shown to the user or not.
            this.errorString = error.msg;
            errorMsgElt = (
                <div id={addressValidationId} className="poBoxPXP">
                    <ul className="tipText tipHintText open error">
                        <li>
                            {this.errorString}
                        </li>
                    </ul>
                </div>
            );
        }

        // Section to show error when email already exists in PayPal system.
        if (touched && error && error.className === 'hasError emailUnavailableError') {
            this.errorString = error.msg;
            errorMsgElt = (
                <div id="emailUnavailableValidation" className="bothActiveAndInactiveError">
                    <ul className="tipText tipHintText open error">
                        <li>
                            {this.errorString}
                        </li>
                    </ul>
                </div>
            );
        }

        if (errorMsgElt) {
            return errorMsgElt;
        }

        /************************************************
         * Only active field errors beyond this point
         ************************************************/

        //PO Box Error handling varies slightly from the regular field validation error handling.
        if (POBox) {
            const addressValidationId = `${id}AddressValidation`;
            if (POBox.showToolTip && active) {
                this.errorString = POBox.POBoxMsg;
                errorMsgElt = (
                    <div id={addressValidationId} className="poBoxPXP">
                        <ul className="tipText tipHintText open">
                            <li>
                                {this.errorString}
                            </li>
                        </ul>
                    </div>
                );
            }
        } else if (error && error.fieldType === 'password') {
            const hints = this.showValidationHints(error.msgHints);
            const errorMsg = this.showValidationErrors(error.msgRestrictions, id);

            if (errorMsg.length > 0) {
                this.hasError = true;
                this.errorString = JSON.stringify(this.getValidationErrorsToShow(error.msgRestrictions));
                errorMsgElt = (
                    <div className="help-error-wrapper" id={`${id}-help-error`}>
                        {errorMsg}
                    </div>
                );
            } else if (hints.length > 0) {
                this.errorString = JSON.stringify(this.getValidationHintsToShow(error.msgHints));
                errorMsgElt = (
                    <div className="help-information open" id={`${id}-help-information`}>
                        <ul>
                            {hints}
                        </ul>
                    </div>
                );
            }
        } else if (validationError) {
            this.errorString = validationError.msg;
            errorMsgElt = (
                <div className="help-error-wrapper" id={`${id}-help-error`}>
                    <p className="help-error error-empty open" id={`${id}Format'`}>{this.errorString}</p>
                </div>
            );
        } else if (error) {
            this.errorString = error.msgKey ? intl.formatMessage({ id: error.msgKey }) : error.msg;
            errorMsgElt = (
                <div className="help-error-wrapper" id={`${id}-help-error`}>
                    <p className="help-error error-empty open" id={`${id}Format'`}>
                        {this.errorString}
                    </p>
                </div>
            );
        }

        return active ? errorMsgElt : '';
    };

    getWrapClassName = () => {
        const { validationError, meta: { touched, error } } = this.props;
        let wrapClassName = 'inputField textInput';

        if (touched && error && error.className){
            wrapClassName = `${wrapClassName} ${error.className}`;
        }

        if (touched && validationError){
            wrapClassName = `${wrapClassName} ${validationError.className}`;
        }

        if (touched && error && error.fieldType === 'password') {
            wrapClassName = `${wrapClassName} hasError`;
        }

        return wrapClassName;
    };

    render() {
        const {
            input, id, type = 'text', arialabel, ariadescribedby, autoCapitalize, required, autoComplete, autoFocus, autoCorrect,
            disabled, maxLength, mask, showTickMark
        } = this.props;

        let inputField;
        const wrapClassName = this.getWrapClassName();
        const labelElt = this.getLabelElt();
        const errorMsgElt = this.getErrorMsgElt();
        const tickMark = showTickMark ? (<span className="tickmark show" role="alert" />) : '';
        const hasError = _.includes(wrapClassName, 'hasError') || !!this.hasError;

        if (mask) {
            inputField = (
                <InputMask
                    {...input}
                    id={id}
                    type={type}
                    mask={mask}
                    maxLength={maxLength}
                    autoCapitalize={autoCapitalize}
                    autoComplete={autoComplete}
                    autoFocus={autoFocus}
                    autoCorrect={autoCorrect}
                    disabled={disabled}
                    maskChar="_"
                    alwaysShowMask={false}
                    aria-label={arialabel}
                    aria-describedby={ariadescribedby}
                    data-errormsg={this.errorString || ''}
                    data-errorshowmsg={!!errorMsgElt}
                    data-haserror={hasError}
                />
            );
        } else {
            inputField = (
                <input
                    {...input}
                    id={id}
                    type={type}
                    maxLength={maxLength}
                    autoCapitalize={autoCapitalize}
                    required={required}
                    autoComplete={autoComplete}
                    autoFocus={autoFocus}
                    autoCorrect={autoCorrect}
                    disabled={disabled}
                    aria-label={arialabel}
                    aria-describedby={ariadescribedby}
                    data-errormsg={this.errorString || ''}
                    data-errorshowmsg={!!errorMsgElt}
                    data-haserror={hasError}
                />
            );
        }

        return (
            <div className="TextField form-field">
                <div className={wrapClassName}>
                    {inputField}
                    {labelElt}
                    {errorMsgElt}
                    {tickMark}
                </div>
            </div>
        );
    }
}

export default injectIntl(TextField);
